/**
 * Comprobante de recepción (acta) para imprimir o guardar como PDF.
 *
 * Mismo mecanismo que `trazabilidad-job-pdf.ts`: se arma un HTML con estilos
 * de impresión y se abre en una ventana nueva con el botón "Imprimir / Guardar
 * PDF" del navegador. Como la recepción no tiene vínculo con Plataforma, este
 * documento y el export Excel son el respaldo de lo que se confirmó en ANMAT.
 */

import type { ActaRecepcion, SerieActa } from '@/api/contracts/recepcion-acta';
import {
  ESTADO_ACTA_LABEL,
  ESTADO_SERIE_ACTA_LABEL,
  MOTIVO_NO_RECIBIDA_LABEL,
} from '@/domain/types';
import { LOGO_EVEREX_DATA_URI } from '@/assets/logo-everex';

interface GenerarInput {
  acta: ActaRecepcion;
  impresoPor?: string | null;
}

function esc(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtFechaHora(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}`;
}

function fmtFecha(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : String(value);
}

const DESTINO_LABEL: Record<string, string> = { a: 'Disponible', na: 'No disponible' };

function filaSerie(s: SerieActa): string {
  const estado =
    s.estado === 'no_recibida' && s.noRecibidaMotivo
      ? `${ESTADO_SERIE_ACTA_LABEL.no_recibida} · ${MOTIVO_NO_RECIBIDA_LABEL[s.noRecibidaMotivo]}`
      : ESTADO_SERIE_ACTA_LABEL[s.estado];
  const detalle =
    s.estado === 'error'
      ? s.errorMessage
      : s.estado === 'no_recibida'
        ? s.noRecibidaDetalle
        : s.estado === 'trazado'
          ? s.idTransacTraza
            ? `Conf. ANMAT ${s.idTransacTraza}`
            : null
          : null;
  return `
    <tr>
      <td class="mono">${esc(s.gtin ?? '—')}</td>
      <td>${esc(s.nombre ?? '—')}</td>
      <td class="mono">${esc(s.lote ?? '—')}</td>
      <td class="mono">${esc(s.numeroSerial ?? '—')}</td>
      <td>${esc(fmtFecha(s.vencimiento))}</td>
      <td>${esc(estado)}</td>
      <td class="small">${esc(detalle ?? '')}</td>
    </tr>`;
}

function tabla(titulo: string, series: readonly SerieActa[]): string {
  if (series.length === 0) return '';
  return `
  <h2>${esc(titulo)} <span class="count">${series.length}</span></h2>
  <table>
    <thead><tr>
      <th>GTIN</th><th>Producto</th><th>Lote</th><th>N° serie</th><th>Vto.</th><th>Estado</th><th>Detalle</th>
    </tr></thead>
    <tbody>${series.map(filaSerie).join('')}</tbody>
  </table>`;
}

function buildHtml({ acta, impresoPor }: GenerarInput): string {
  const documento = acta.numeroRemito
    ? `Remito ${acta.numeroRemito}`
    : acta.numeroFactura
      ? `Factura ${acta.numeroFactura}`
      : `Acta #${acta.id}`;
  const confirmadas = acta.series.filter((s) => s.estado === 'trazado');
  const noRecibidas = acta.series.filter((s) => s.estado === 'no_recibida');
  const rechazadas = acta.series.filter((s) => s.estado === 'error');
  const anuladas = acta.series.filter((s) => s.estado === 'anulada');
  const abiertas = acta.series.filter((s) => s.estado === 'pendiente' || s.estado === 'marcada');

  const sobrantes =
    acta.sobrantes.length === 0
      ? ''
      : `
  <h2>Unidades no declaradas por el emisor (sobrantes) <span class="count">${acta.sobrantes.length}</span></h2>
  <p class="nota">Llegaron físicamente pero ANMAT no las lista para este documento: no se pueden confirmar ni entran al stock. Hay que reclamarle al emisor que las informe.</p>
  <table>
    <thead><tr><th>GTIN</th><th>Lote</th><th>N° serie</th><th>Vto.</th><th>Registrado</th></tr></thead>
    <tbody>${acta.sobrantes
      .map(
        (x) => `<tr>
        <td class="mono">${esc(x.gtin ?? '—')}</td><td class="mono">${esc(x.lote ?? '—')}</td>
        <td class="mono">${esc(x.numeroSerial)}</td><td>${esc(fmtFecha(x.vencimiento))}</td>
        <td class="small">${esc(fmtFechaHora(x.createdAt))} · ${esc(x.usuario ?? '—')}</td></tr>`,
      )
      .join('')}</tbody>
  </table>`;

  return `<!DOCTYPE html>
<html lang="es"><head>
<meta charset="UTF-8">
<title>Acta de recepción — ${esc(documento)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; margin: 0; padding: 24px 32px; color: #1B2A4A; font-size: 10.5pt; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .header { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #E05206; padding-bottom: 10px; margin-bottom: 14px; }
  .header img { height: 34px; }
  .header h1 { font-size: 16pt; margin: 0; color: #003057; }
  .header .doc { text-align: right; font-size: 10pt; color: #555; }
  .header .doc b { display: block; font-size: 14pt; color: #003057; font-family: ui-monospace, Menlo, monospace; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin: 8px 0 14px; font-size: 10pt; }
  .grid div span { color: #777; display: inline-block; min-width: 130px; }
  .resumen { display: flex; gap: 8px; flex-wrap: wrap; margin: 6px 0 12px; }
  .chip { border-radius: 999px; padding: 2px 10px; font-size: 9.5pt; background: #eef1f6; }
  .chip.ok { background: #dcfce7; } .chip.bad { background: #fee2e2; } .chip.warn { background: #fef9c3; }
  h2 { font-size: 11.5pt; margin: 16px 0 6px; color: #003057; break-after: avoid; }
  .count { font-weight: normal; color: #777; }
  .nota { font-size: 9.5pt; color: #555; margin: 0 0 6px; }
  table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
  th { text-align: left; background: #f3f4f6; padding: 4px 6px; border-bottom: 1px solid #d1d5db; font-weight: 600; }
  td { padding: 3px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
  tr { break-inside: avoid; }
  .mono { font-family: ui-monospace, Menlo, monospace; font-size: 9pt; }
  .small { font-size: 8.5pt; color: #555; }
  .footer { margin-top: 18px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 8.5pt; color: #666; display: flex; justify-content: space-between; }
  .print-btn { position: fixed; top: 12px; right: 12px; padding: 8px 14px; background: #E05206; color: #fff; border: 0; border-radius: 6px; font-size: 13px; cursor: pointer; }
  @media print { .print-btn { display: none; } body { padding: 0; } }
</style></head>
<body>
<button class="print-btn" onclick="window.print()">Imprimir / Guardar PDF</button>
<div class="header">
  <div style="display:flex;align-items:center;gap:14px">
    <img src="${LOGO_EVEREX_DATA_URI}" alt="Everex">
    <h1>Acta de recepción · SNT ANMAT</h1>
  </div>
  <div class="doc"><span>Documento del emisor</span><b>${esc(documento)}</b></div>
</div>

<div class="grid">
  <div><span>Emisor</span>${esc(acta.razonSocialOrigen ?? '—')} <span class="mono">(${esc(acta.glnOrigen ?? '—')})</span></div>
  <div><span>Receptor</span>${esc(acta.razonSocialDestino ?? '—')} <span class="mono">(${esc(acta.glnDestino ?? '—')})</span></div>
  <div><span>Remito</span>${esc(acta.numeroRemito ?? '—')}</div>
  <div><span>Factura</span>${esc(acta.numeroFactura ?? '—')}</div>
  <div><span>Estado del acta</span>${esc(ESTADO_ACTA_LABEL[acta.estado])}</div>
  <div><span>Destino del stock</span>${esc(acta.stockDestino ? (DESTINO_LABEL[acta.stockDestino] ?? acta.stockDestino) : '—')}</div>
  <div><span>Abierta</span>${esc(fmtFechaHora(acta.createdAt))}</div>
  <div><span>Cerrada</span>${esc(fmtFechaHora(acta.cerradoAt))}</div>
  <div><span>Acta N°</span>${acta.id}</div>
  <div><span>Series declaradas</span>${acta.totales.total}</div>
</div>

<div class="resumen">
  <span class="chip ok">Confirmadas ${acta.totales.trazado}</span>
  <span class="chip warn">No recibidas ${acta.totales.noRecibida}</span>
  <span class="chip bad">Rechazadas por ANMAT ${acta.totales.error}</span>
  <span class="chip">Anuladas por el emisor ${acta.totales.anulada}</span>
  <span class="chip">Sobrantes ${acta.sobrantes.length}</span>
  ${abiertas.length > 0 ? `<span class="chip warn">Sin resolver ${abiertas.length}</span>` : ''}
</div>

${tabla('Series confirmadas en ANMAT', confirmadas)}
${tabla('Series no recibidas', noRecibidas)}
${tabla('Series rechazadas por ANMAT', rechazadas)}
${tabla('Series anuladas por el emisor', anuladas)}
${tabla('Series sin resolver', abiertas)}
${sobrantes}

<div class="footer">
  <span>Riga Traza · Everex Argentina S.A. · Acta #${acta.id}</span>
  <span>Impreso ${esc(fmtFechaHora(new Date().toISOString()))}${impresoPor ? ` por ${esc(impresoPor)}` : ''}</span>
</div>
</body></html>`;
}

/** Abre el acta en una ventana nueva lista para imprimir. False si el navegador la bloqueó. */
export function abrirActaRecepcionPdf(input: GenerarInput): boolean {
  const html = buildHtml(input);
  const w = window.open('', '_blank', 'width=1000,height=900');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
